<template>
  <div
    :data-testid="`wrapper-${uid}`"
    :class="componentClass"
    @click="handleClick"
  >
    <div class="sb-toggle__container">
      <label v-if="!hideLabel" :for="uid" class="sb-toggle__label">
        {{ label }}
      </label>
      <span
        v-if="description && labelPosition === 'top' && !hideLabel"
        :id="`sb-toggle-description-${uid}`"
        class="sb-toggle__description"
      >
        {{ description }}
      </span>
      <div class="sb-toggle__input-container">
        <input
          :id="uid"
          :checked="modelValue"
          v-bind="rootProps"
          class="sb-toggle__native"
          type="checkbox"
          :name="name"
          :value="nativeValue"
          :aria-disabled="disabled"
          :readonly="readonly"
          :aria-label="hideLabel ? label : undefined"
          :aria-checked="modelValue"
          role="switch"
          :aria-describedby="
            description ? `sb-toggle-description-${uid}` : undefined
          "
          @click.stop
          @input="handleInput"
        />
        <label
          :for="uid"
          class="sb-toggle__input"
          v-tooltip="tooltipOptions"
        ></label>
      </div>
    </div>
    <span
      v-if="description && labelPosition === 'left' && !hideLabel"
      :id="`sb-toggle-description-${uid}`"
      class="sb-toggle__description"
    >
      {{ description }}
    </span>
  </div>
</template>

<script lang="ts" setup>
import { reactiveOmit } from '@vueuse/core'
import { computed, useId, type InputHTMLAttributes } from 'vue'
import { Tooltip as vTooltip } from '../../directives'
import type { ToolTipOptionsInput } from '../../directives/tooltip/lib'

export type ToggleProps = {
  /**
   * Toggle value.
   */
  modelValue: boolean

  /**
   * Toggle label.
   */
  label: string

  /**
   * Toggle aria-label.
   */
  ariaLabel?: string

  /*
   * Toggle label text visibility. Use this prop to hide the label visually.
   */
  hideLabel?: boolean

  /**
   * Toggle native value value.
   */
  nativeValue?: string | number | boolean

  /**
   * Toggle id attribute.
   */
  id?: string

  /**
   * Toggle disabled state. The disable state does not remove the focus of the input.
   */
  disabled?: boolean

  /**
   * Disables interaction with the field.
   */
  readonly?: boolean

  /**
   * Toggle name attribute.
   */
  name?: string

  /**
   * Toggle description. Use this text to add more context to the Toggle.
   */
  description?: string

  /**
   * Defines the position of the label.
   */
  labelPosition?: 'top' | 'left'

  /**
   * Toggle size.
   */
  size?: 'small' | 'normal'

  /**
   * Tooltip for the toggle.
   */
  tooltipOptions?: string | ToolTipOptionsInput
} & /* @vue-ignore */ Omit<InputHTMLAttributes, 'onClick'>

const props = withDefaults(defineProps<ToggleProps>(), {
  modelValue: false,
  label: '',
  nativeValue: undefined,
  id: undefined,
  disabled: false,
  name: undefined,
  description: undefined,
  labelPosition: 'left',
})

const rootProps = reactiveOmit(props, [
  'label',
  'id',
  'checked',
  'description',
  'labelPosition',
  'disabled',
  'size',
  'tooltipOptions',
])

const emit = defineEmits<{
  /**
   * Emitted when the toggle value changes.
   */
  (e: 'update:modelValue', value: boolean): void

  /**
   * Emitted when the toggle is clicked.
   */
  (e: 'click', payload: { isDisabled: boolean }): void
}>()

const uid = computed(() => props.id ?? useId())

const componentClass = computed(() => {
  return {
    'sb-toggle': true,
    'sb-toggle--label-on-top': props.labelPosition === 'top',
    'sb-toggle--with-description': props.description,
    'sb-toggle--small': props.size === 'small',
  }
})

function handleInput(event: Event) {
  if (props.disabled || props.readonly) {
    event.preventDefault()
    event.stopPropagation()
    return
  }
  emit('update:modelValue', !props.modelValue)
}

function handleClick() {
  emit('click', { isDisabled: props.disabled })
}
</script>
